import React, { useEffect, useRef, useState } from "react";
import { getCachedImageUrl, peekCachedImageUrl } from "../lib/imageCache";

/**
 * Drop-in <img> replacement that serves through the in-memory image cache
 * (see lib/imageCache.js). For auth-gated `/api/*` images — which Cloudflare
 * forces `no-store` on — this makes revisits instant instead of a full
 * re-download. External/public URLs pass straight through.
 *
 * All standard <img> props (className, alt, onError, draggable, data-testid…)
 * are forwarded unchanged.
 */
export function CachedImage({ src, alt = "", ...props }) {
  // Initialise from a synchronous peek so a cache hit paints with no flash.
  const [resolved, setResolved] = useState(() => peekCachedImageUrl(src));
  const srcRef = useRef(src);
  srcRef.current = src;

  useEffect(() => {
    if (!src) { setResolved(null); return; }
    const hit = peekCachedImageUrl(src);
    if (hit) { setResolved(hit); return; }

    let active = true;
    setResolved(null);
    getCachedImageUrl(src).then((u) => {
      if (active && srcRef.current === src) setResolved(u);
    });
    return () => { active = false; };
  }, [src]);

  // Until resolved (first fetch of an /api image), render a src-less img so
  // layout/dimensions hold without firing a broken-image request.
  return <img {...props} src={resolved || undefined} alt={alt} />;
}

export default CachedImage;
